import { Component, computed, inject, input, InputSignal } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";

import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { DialogWrapperComponent } from "@bitwarden/web-vault/app/components/wrappers/dialog-wrapper/dialog-wrapper.component";
import { ClassificationAddEditComponent } from "@bitwarden/web-vault/app/gloss/manage/manage-classifications/classification-add-edit/classification-add-edit.component";
import { OnBackDirective } from "@bitwarden/web-vault/app/directives/onBack.directive";
import { DEFAULT_CLASSIFICATION_ID } from "@bitwarden/web-vault/app/models/constants/classification.constants";
import { ConfirmationEnum } from "@bitwarden/web-vault/app/models/enum/confirmation.enum";
import { NotificationEnum } from "@bitwarden/web-vault/app/models/enum/notification.enum";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { TransactionStoreService } from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.service";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { mobileSlideInConfig } from "@bitwarden/web-vault/app/components/dialog/mobile-slide-in-dialog";

@Component({
  selector: "app-classifications-details",
  templateUrl: "./classifications-details.component.html",
})
export class ClassificationsDetailsComponent extends OnBackDirective {
  private transactionStoreService: TransactionStoreService = inject(TransactionStoreService);
  private userStoreService: UserStoreService = inject(UserStoreService);
  private notifyService: ConfirmationDialogService = inject(ConfirmationDialogService);
  private i18nService = inject(I18nService);

  protected readonly DEFAULT_CLASSIFICATION_ID = DEFAULT_CLASSIFICATION_ID;

  protected selectedClassificationId: InputSignal<string> = input("");
  selectedClassification = computed(() =>
    this.userStoreService.classifications
      .classificationViews()
      ?.find((cl) => cl.id === this.selectedClassificationId()),
  );

  linkedTransactionCount = computed(
    () =>
      this.transactionStoreService.enrichTransaction
        .classificationCountMap()
        .get(this.selectedClassification()?.id) ?? 0,
  );

  constructor(public dialog: MatDialog) {
    super();
  }

  async deleteClassification() {
    if (this.linkedTransactionCount() > 0) {
      this.notifyService.notify(
        NotificationEnum.warning,
        "notice",
        this.i18nService.t(
          "classificationCannotDeleteWhenLinkedTransactionsExist",
          this.linkedTransactionCount(),
        ),
        false,
      );
      return;
    }

    const confirmed = await this.notifyService.confirmFor(ConfirmationEnum.deletingClassification);
    if (!confirmed) {
      return;
    }

    this.userStoreService.classifications
      .delete(this.selectedClassification())
      .then(() => {
        this.notifyService.notify(NotificationEnum.success, "succeeded", "deletedSuccessfully");
        this.handleBackClick();
      })
      .catch(() => {
        this.notifyService.notify(NotificationEnum.error, "errorOccurred", "somethingWentWrong");
      });
  }

  openEditClassificationForm() {
    this.dialog.open(DialogWrapperComponent, {
      ...mobileSlideInConfig("no-background-dialog"),
      disableClose: true,
      data: {
        title: "classificationDetails",
        showBackButton: false,
        component: ClassificationAddEditComponent,
        fullyCustomised: true,
        showHeaderCloseButton: true,
        componentInputs: {
          classification: this.selectedClassification(),
          actionSucceeded: this.editClassificationSuccessful.bind(this),
        },
      },
    });
  }

  editClassificationSuccessful(response: {
    newClassificationText: string;
    successfulText: string;
  }) {
    this.notifyService.notify(NotificationEnum.success, "succeeded", response.successfulText);
  }

  editClassification() {
    this.openEditClassificationForm();
  }
}
